import type {
	TimesheetBucket as APITimesheetBucket,
	TimesheetDay as APITimesheetDay,
	TimesheetResponse as APITimesheetResponse,
	TimesheetRow as APITimesheetRow,
} from "@/adapters/api";
import type {
	Timesheet,
	TimesheetBucket,
	TimesheetDay,
	TimesheetRow,
} from "../types";

function toBucket(bucket: APITimesheetBucket): TimesheetBucket {
	return {
		minutes: bucket.minutes,
		pct: bucket.pct,
		equivalentMinutes: bucket.equivalent_minutes,
	};
}

export function toTimesheetDay(day: APITimesheetDay): TimesheetDay {
	return {
		date: day.date,
		kind: day.kind,
		status: day.status,
		workedMinutes: day.worked_minutes,
		overtimeMinutes: day.overtime_minutes,
		overtimePct: day.overtime_pct,
		overridden: day.overridden,
	};
}

export function toTimesheetRow(row: APITimesheetRow): TimesheetRow {
	return {
		worker: {
			id: row.worker.id,
			name: row.worker.name,
			jobTitle: row.worker.job_title ?? "",
			linked: row.worker.linked,
		},
		daysPresent: row.days_present,
		daysAbsent: row.days_absent,
		daysMedicalLeave: row.days_medical_leave,
		daysDayOff: row.days_day_off,
		daysPending: row.days_pending,
		workedMinutes: row.worked_minutes,
		normalMinutes: row.normal_minutes,
		missingMinutes: row.missing_minutes,
		overtime: {
			weekday: toBucket(row.overtime.weekday),
			saturday: toBucket(row.overtime.saturday),
			sunday: toBucket(row.overtime.sunday),
			holiday: toBucket(row.overtime.holiday),
		},
		overtimeMinutes: row.overtime_minutes,
		overtimeEquivalentMinutes: row.overtime_equivalent_minutes,
		totalEquivalentMinutes: row.total_equivalent_minutes,
		days: (row.days ?? []).map(toTimesheetDay),
	};
}

export function toTimesheet(response: APITimesheetResponse): Timesheet {
	return {
		month: response.month,
		worksite: {
			id: response.worksite.id,
			name: response.worksite.name,
			timezone: response.worksite.timezone,
		},
		settings: {
			weekdayPct: response.settings.weekday_pct,
			saturdayPct: response.settings.saturday_pct,
			sundayPct: response.settings.sunday_pct,
			holidayPct: response.settings.holiday_pct,
		},
		holidays: (response.holidays ?? []).map((holiday) => ({
			date: holiday.date,
			name: holiday.name,
		})),
		rows: (response.rows ?? []).map(toTimesheetRow),
		totals: {
			workedMinutes: response.totals.worked_minutes,
			overtimeMinutes: response.totals.overtime_minutes,
			overtimeEquivalentMinutes: response.totals.overtime_equivalent_minutes,
		},
	};
}
